import { useCallback } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useCurrentUser } from '~/lib/auth/use-current-user'
import { useDashboardChannel } from '~/lib/socket/use-dashboard-channel'
import { toIsoDate } from '~/lib/time/format'
import { CheckinList } from './checkin-list'
import { HoursSummary } from './hours-summary'
import { MoodStrip } from './mood-strip'
import { OooLog } from './ooo-log'
import { TimeOffShortcuts } from './time-off-shortcuts'
import { useInvalidatePerson, usePersonSnapshot } from './use-person-snapshot'
import styles from './person-page.module.scss'

const DAYS = 30

export function PersonPage() {
  const { slackId } = useParams<{ slackId: string }>()
  const { data: me } = useCurrentUser()
  const tz = me?.user.timezone ?? 'America/Chicago'
  const isAdmin = me?.user.is_admin ?? false

  const person = usePersonSnapshot(slackId, DAYS, tz)

  const invalidate = useInvalidatePerson()
  const onSocketChange = useCallback(() => {
    if (slackId) invalidate(slackId, DAYS, tz)
  }, [invalidate, slackId, tz])
  useDashboardChannel(me?.socket_token ?? null, onSocketChange)

  const handleTimeOffCreated = useCallback(() => {
    if (slackId) invalidate(slackId, DAYS, tz)
  }, [invalidate, slackId, tz])

  if (!slackId) return null

  return (
    <div className={styles.page}>
      <div className={styles.chromeRow}>
        <Link
          to="/calendar"
          className={styles.back}
          aria-label="Back to calendar"
        >
          ← Calendar
        </Link>
      </div>

      {person.isLoading && (
        <p className={styles.placeholder} aria-live="polite">
          Loading engineer…
        </p>
      )}

      {person.isError && (
        <p className={styles.placeholder} role="alert">
          Couldn&apos;t load this engineer.{' '}
          <button
            type="button"
            className={styles.retry}
            onClick={() => person.refetch()}
          >
            Retry →
          </button>
        </p>
      )}

      {person.data && (
        <main className={styles.body}>
          <header className={styles.header}>
            <div>
              <h1 className={styles.heading}>
                {person.data.user.display_name ??
                  person.data.user.slack_user_id}
              </h1>
              <p className={styles.subhead}>
                {person.data.user.slack_user_id} · {person.data.user.timezone}
              </p>
            </div>
            <div className={styles.streakChip} aria-label="current streak">
              <span className={`${styles.streakValue} mono`}>
                {person.data.stats.current_streak}
              </span>
              <span className={styles.streakLabel}>day streak</span>
            </div>
          </header>

          <section aria-label="30-day pattern">
            <MoodStrip
              days={person.data.days}
              todayIso={toIsoDate(new Date())}
              userId={person.data.user.id}
            />
          </section>

          <section>
            <HoursSummary stats={person.data.stats} />
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionHeading}>Recent check-ins</h2>
            <CheckinList checkins={person.data.checkins} />
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionHeading}>Time off</h2>
            {isAdmin ? (
              <TimeOffShortcuts
                slackId={person.data.user.slack_user_id}
                displayName={
                  person.data.user.display_name ??
                  person.data.user.slack_user_id
                }
                todayIso={toIsoDate(new Date())}
                onCreated={handleTimeOffCreated}
              />
            ) : null}
            <OooLog periods={person.data.time_off} />
          </section>
        </main>
      )}
    </div>
  )
}
